"use client";

import type { Easing, TargetAndTransition, Variants } from "framer-motion";
import type { CSSProperties, ReactNode, Ref } from "react";
import type { MotionConfig } from "@/components/landing/registry/types";
import { motion, useInView } from "framer-motion";
import { Children, memo, useId, useRef } from "react";

const EASING: Record<MotionConfig["easing"], Easing | [number, number, number, number]> = {
	easeOut: [0.22, 1, 0.36, 1],
	easeInOut: [0.65, 0, 0.35, 1],
	linear: "linear",
	spring: [0.34, 1.56, 0.64, 1],
};

function hiddenState(type: MotionConfig["type"]) {
	switch (type) {
		case "fade":
			return { opacity: 0 };
		case "slideUp":
			return { opacity: 0, y: 28 };
		case "slideDown":
			return { opacity: 0, y: -28 };
		case "slideLeft":
			return { opacity: 0, x: 28 };
		case "slideRight":
			return { opacity: 0, x: -28 };
		case "scale":
			return { opacity: 0, scale: 0.92 };
		case "blur":
			return { opacity: 0, filter: "blur(8px)" };
		case "rotate":
			return { opacity: 0, rotate: -6, scale: 0.98 };
		case "flip":
			return { opacity: 0, rotateX: -35 };
		default:
			return { opacity: 0 };
	}
}

const VISIBLE_STATE = {
	opacity: 1,
	y: 0,
	x: 0,
	scale: 1,
	filter: "blur(0px)",
	rotate: 0,
	rotateX: 0,
};

// `hiddenState` spelled as CSS for the `eager` path; the two must stay in step.
const HIDDEN_TRANSFORM: Record<MotionConfig["type"], string> = {
	fade: "none",
	slideUp: "translateY(28px)",
	slideDown: "translateY(-28px)",
	slideLeft: "translateX(28px)",
	slideRight: "translateX(-28px)",
	scale: "scale(0.92)",
	blur: "none",
	rotate: "rotate(-6deg) scale(0.98)",
	flip: "rotateX(-35deg)",
};

function cssEasing(easing: MotionConfig["easing"]): string {
	const e = EASING[easing];
	return Array.isArray(e) ? `cubic-bezier(${e.join(",")})` : String(e);
}

function eagerStyle(cfg: MotionConfig, delay: number, style?: CSSProperties): CSSProperties {
	return {
		...style,
		"--reveal-from-transform": HIDDEN_TRANSFORM[cfg.type] ?? "none",
		"--reveal-from-filter": cfg.type === "blur" ? "blur(8px)" : "none",
		"--reveal-duration": `${cfg.duration}ms`,
		"--reveal-delay": `${delay}ms`,
		"--reveal-easing": cssEasing(cfg.easing),
	} as CSSProperties;
}

const HOVER_STATE: Record<MotionConfig["hover"], TargetAndTransition> = {
	none: {},
	lift: { y: -4 },
	scale: { scale: 1.02 },
	glow: { boxShadow: "0 0 0 3px var(--ring)" },
};

function buildVariants(motionConfig: MotionConfig, delayOffset = 0): Variants {
	return {
		hidden: hiddenState(motionConfig.type),
		visible: {
			...VISIBLE_STATE,
			transition: {
				duration: motionConfig.duration / 1000,
				delay: (motionConfig.delay + delayOffset) / 1000,
				ease: EASING[motionConfig.easing],
			},
		},
	};
}

interface MotionRevealProps {
	motion: MotionConfig;
	children: ReactNode;
	className?: string;
	style?: CSSProperties;
	as?: "div" | "section";
	replayKey?: string | number;
	/**
	 * Added on top of `motion.delay` — lets a section stagger its own
	 * internal blocks (e.g. header then card then bullets) while still using
	 * one shared Motion-tab config for type/duration/easing.
	 */
	delayOffset?: number;
	/**
	 * Above-the-fold content (the hero): play the entrance as a CSS animation
	 * from first paint instead of after hydration. Framer's `initial="hidden"`
	 * serialises `opacity:0` into the server HTML, so the LCP element stayed
	 * invisible until every chunk had downloaded and React had hydrated.
	 */
	eager?: boolean;
}

// Print escape hatch: framer-motion drives entrance state through inline
// `style` (opacity/transform/filter), which a plain CSS rule cannot beat
// without `!important`. `Reveal.tsx`'s CSS-only equivalent forces its
// `.reveal` class back to its resting state under `@media print` in
// `globals.css`; `MotionReveal` has no such hook (landing-studio never added
// one), so it carries its own scoped stylesheet here rather than reaching
// into `globals.css` — named after this component the same way `.reveal` is
// named after `Reveal`, so an element still mid-animation (or never
// scrolled into view) still prints in full.
const PRINT_CSS = `@media print {
	.motion-reveal {
		opacity: 1 !important;
		transform: none !important;
		filter: none !important;
	}
}`;

/**
 * Framer-motion entrance wrapper driven by the Studio's Motion tab. Defaults
 * (slideUp / 700ms / easeOut / trigger once) intentionally reproduce the
 * previous CSS-only `Reveal` look, so nothing changes visually until an admin
 * edits Motion. `stagger` > 0 staggers direct children instead of animating
 * this element as one block.
 *
 * ⚠️ Drives visibility through the `useInView` hook and an explicit
 * `animate` prop, not the `whileInView` prop. Measured on this exact stack
 * (framer-motion 13.1.1, React 19.2, Next 16/Turbopack): `whileInView`'s
 * `Feature`-based observer (`motion/features/viewport`) never fired for
 * content already in the viewport on first paint — every `.motion-reveal`
 * node stayed pinned at its `hidden` variant (`opacity:0`) indefinitely, on
 * a real browser and confirmed not a headless-only artifact, surviving
 * scroll and resize. A hand-rolled `IntersectionObserver` on the same node
 * fired immediately (`isIntersecting: true` on load), so the browser API is
 * fine — the bug is in that one Feature's wiring. `useInView` goes through a
 * separate implementation (`render/dom/viewport`'s `inView`, its own
 * dedicated observer per call, no shared-observer cache) and does not
 * reproduce it.
 */
function InViewReveal(prop: MotionRevealProps) {
	const { motion: cfg, children, className, style, as = "div", replayKey, delayOffset = 0 } = prop;
	const Tag = as === "section" ? motion.section : motion.div;
	const ref = useRef<HTMLElement>(null);
	const isInView = useInView(ref, { once: cfg.viewportTrigger === "once", amount: 0.15 });
	const animateState = isInView ? "visible" : "hidden";
	const hoverAnimate = cfg.hover !== "none" ? HOVER_STATE[cfg.hover] : undefined;
	const idleClass =
		cfg.idle === "float" ? "jana-float" : cfg.idle === "pulse" ? "jana-idle-pulse" : "";

	if (cfg.stagger > 0) {
		const container: Variants = {
			hidden: {},
			visible: {
				transition: {
					staggerChildren: cfg.stagger / 1000,
					delayChildren: (cfg.delay + delayOffset) / 1000,
				},
			},
		};
		const item = buildVariants({ ...cfg, delay: 0 });
		return (
			<>
				<style>{PRINT_CSS}</style>
				<Tag
					key={replayKey}
					ref={ref as Ref<HTMLDivElement>}
					className={["motion-reveal", className, idleClass].filter(Boolean).join(" ")}
					style={style}
					variants={container}
					initial="hidden"
					animate={animateState}
				>
					{Children.map(children, (child) => (
						<motion.div variants={item} whileHover={hoverAnimate}>
							{child}
						</motion.div>
					))}
				</Tag>
			</>
		);
	}

	return (
		<>
			<style>{PRINT_CSS}</style>
			<Tag
				key={replayKey}
				ref={ref as Ref<HTMLDivElement>}
				className={["motion-reveal", className, idleClass].filter(Boolean).join(" ")}
				style={style}
				variants={buildVariants(cfg, delayOffset)}
				initial="hidden"
				animate={animateState}
				whileHover={hoverAnimate}
			>
				{children}
			</Tag>
		</>
	);
}

/**
 * The `eager` path: the same Motion-tab config expressed as CSS custom
 * properties on `.motion-reveal-eager` (globals.css), so the entrance runs from
 * the server HTML with no JavaScript. `backwards` fill hides the element only
 * during its delay; afterwards it rests on its own styles, which is what lets
 * the hover classes move it. Idle motion sits on an inner wrapper because it is
 * an `animation` too and would replace the entrance on the same element; under
 * stagger the container has no entrance of its own, so it keeps the idle class.
 */
function EagerReveal(prop: MotionRevealProps) {
	const { motion: cfg, children, className, style, as = "div", replayKey, delayOffset = 0 } = prop;
	const Tag = as;
	const idleClass =
		cfg.idle === "float" ? "jana-float" : cfg.idle === "pulse" ? "jana-idle-pulse" : "";
	const hover = cfg.hover !== "none" ? cfg.hover : undefined;
	const delay = cfg.delay + delayOffset;
	const idle = (node: ReactNode) => (idleClass ? <div className={idleClass}>{node}</div> : node);

	if (cfg.stagger > 0) {
		return (
			<>
				<style>{PRINT_CSS}</style>
				<Tag
					key={replayKey}
					className={["motion-reveal", className, idleClass].filter(Boolean).join(" ")}
					style={style}
				>
					{Children.map(children, (child, i) => (
						<div
							className="motion-reveal-eager"
							data-hover={hover}
							style={eagerStyle(cfg, delay + i * cfg.stagger)}
						>
							{child}
						</div>
					))}
				</Tag>
			</>
		);
	}

	return (
		<>
			<style>{PRINT_CSS}</style>
			<Tag
				key={replayKey}
				className={["motion-reveal", "motion-reveal-eager", className].filter(Boolean).join(" ")}
				data-hover={hover}
				style={eagerStyle(cfg, delay, style)}
			>
				{idle(children)}
			</Tag>
		</>
	);
}

function MotionRevealImpl(prop: MotionRevealProps) {
	return prop.eager ? <EagerReveal {...prop} /> : <InViewReveal {...prop} />;
}

export const MotionReveal = memo(MotionRevealImpl);

/**
 * Stable key generator so <Preview> tabs can force a MotionReveal remount
 * ("Replay") without callers needing to manage their own counters.
 */
export function useReplayKey(seed: number): string {
	const id = useId();
	return `${id}-${seed}`;
}
